import { makeStyles } from '@material-ui/core';
import { Grid, Checkbox, FormControlLabel, TextField } from '@mui/material';
import { KeyboardDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import React, { useEffect } from 'react';
import Autocomplete2 from 'components/Autocomplete2';
import { useSelector } from 'react-redux';

const useStyles = makeStyles((theme) => ({
  reportContainer: {
    display: 'flex',
  },
  reportSelection: {
    flex: 0.3,
  },
  reportFilter: {
    flex: 0.7,
  },
  report: {
    paddingBottom: '4px',
  },
  timefilterfield: {
    marginLeft: '0px',
    marginRight: '0px',
    marginBottom: '0px',
    marginTop: '15px',
  },
  filterfield: {
    marginLeft: '0px',
    marginRight: '0px',
    marginBottom: '8px',
    marginTop: '0px',
  },
  targetfilterfield: {
    marginLeft: '0px !important',
    marginRight: '0px !important',
    marginBottom: '0px !important',
    marginTop: '15px !important',
  },
}));

// Priority order: earlier in the array = higher priority
const TIME_STAGE_ORDER = ['Started' ,'Received', 'Queued', 'Dispatched', 'Enroute', 'Arrived', 'UnderCont', 'Completed'];
const TIME_FORMAT_OPTIONS = [{ Code: 'SECONDS' }, { Code: 'MINUTES' }];

const stageIndex = (code) => TIME_STAGE_ORDER.indexOf(code);

const CADEventsData = (props) => {
  const classes = useStyles();
  const { reportForm, handleReportFormChange, ReportID } = props;
  const formValue = reportForm[ReportID] || {};
  const agencies = useSelector((state) => state.dictionary?.Agencies ?? []);
  const agenciesList = agencies.map(item => ({
    Code: item.AgencyID
  }));
  const agency = { Code: useSelector((state) => state.user.userData.user.AgencyID).toString() };

  useEffect(() => {
    const defaults = {};
    if (!formValue.agency) defaults.agency = agency;
    if (!formValue.timeFormat) defaults.timeFormat = { Code: 'SECONDS' };
    //if (!formValue.timeFrom) defaults.timeFrom = { Code: 'Received' };
    //if (!formValue.timeTo) defaults.timeTo = { Code: 'Dispatched' };
    if (formValue.isEventsRequired === undefined) defaults.isEventsRequired = false;
  
    const keys = Object.keys(defaults);
    if (keys.length > 0) {
      const [firstKey, ...restKeys] = keys;
      const restValues = restKeys.reduce((acc, key) => {
        acc[key] = defaults[key];
        return acc;
      }, {});
      handleReportFormChange(ReportID, firstKey, defaults[firstKey], restValues);
    }
  }, [ReportID]);


  const targetError = !formValue.target || Number(formValue.target) <= 0 || !Number.isInteger(Number(formValue.target));
  const handleTargetChange = (e) => {
    const val = e.target.value;
    if (val === '' || /^[1-9][0-9]*$/.test(val)) {
      handleReportFormChange(ReportID, 'target', val);
    }
  };

  const timeFromCode = formValue.timeFrom?.Code ?? null;
  const timeToCode = formValue.timeTo?.Code ?? null;

  const timeToOptions = (timeFromCode
    ? TIME_STAGE_ORDER.filter((stage) => stageIndex(stage) > stageIndex(timeFromCode))
    : TIME_STAGE_ORDER.filter((stage) => stageIndex(stage) > 0)
  ).map((stage) => ({ Code: stage }));

  const timeFromOptions = TIME_STAGE_ORDER
  .filter((stage) => stageIndex(stage) < TIME_STAGE_ORDER.length - 1)
  .map((stage) => ({ Code: stage }));
    
  const timeFromError = !formValue.timeFrom;
  const timeToError = !formValue.timeTo;

  const handleTimeFromChange = (ev, newVal) => {
    handleReportFormChange(ReportID, 'timeFrom', newVal ?? null, { timeTo: null });
  };

  const handleTimeToChange = (ev, newVal) => {
    handleReportFormChange(ReportID, 'timeTo', newVal ?? null);
  };


  return (
    <div className={classes.report}>
      <Grid container justify="left" alignItems="center" spacing={1}>
        <Grid item xl={2} md={2} sm={12} xs={12} className={classes.marginNormal}>
          <Autocomplete2
            options={agenciesList}
            className={classes.filterfield}
            onChange={(ev, newVal) => handleReportFormChange(ReportID, 'agency', newVal)}
            value={formValue.agency ?? null}
            label="Agency"
            compact="True"
            error={!formValue.agency}
          />
        </Grid>

        <Grid item xl={4} md={4} sm={12} xs={12} className={classes.marginNormal}>
          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <KeyboardDatePicker
              disableOpenOnEnter
              className={classes.filterfield}
              autoComplete="off"
              fullWidth
              clearable
              size="small"
              inputVariant="outlined"
              showTodayButton={true}
              variant="dialog"
              format="MM/dd/yyyy"
              id="date-picker-inline-from"
              label="Start Date"
              defaultValue={null}
              value={formValue.startDate || null}
              onChange={(date) =>
                handleReportFormChange(ReportID, 'startDate', date)
              }
              KeyboardButtonProps={{
                'aria-label': 'change date',
              }}
            />
          </MuiPickersUtilsProvider>
        </Grid>

        <Grid item xl={4} md={4} sm={12} xs={12}>
          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <KeyboardDatePicker
              disableOpenOnEnter
              className={classes.filterfield}
              autoComplete="off"
              fullWidth
              clearable
              size="small"
              inputVariant="outlined"
              showTodayButton={true}
              variant="dialog"
              format="MM/dd/yyyy"
              id="date-picker-inline-to"
              label="End Date"
              defaultValue={null}
              value={formValue.endDate || null}
              onChange={(date) =>
                handleReportFormChange(ReportID, 'endDate', date)
              }
              KeyboardButtonProps={{
                'aria-label': 'change date',
              }}
            />
          </MuiPickersUtilsProvider>
        </Grid>

        <Grid item xl={3} md={3} sm={12} xs={12}>
          <Autocomplete2
            options={timeFromOptions}
            className={classes.timefilterfield}
            onChange={handleTimeFromChange}
            value={formValue.timeFrom ?? null}
            label="Time From"
            error={timeFromError}
            helperText={timeFromError ? 'Time From is required' : ' '}
          />
        </Grid>

        <Grid item xl={3} md={3} sm={12} xs={12}>
          <Autocomplete2
            options={timeToOptions}
            className={classes.timefilterfield}
            onChange={handleTimeToChange}
            value={formValue.timeTo ?? null}
            label="Time To"
            error={timeToError}
            helperText={timeToError ? 'Time To is required' : ' '}
          />
        </Grid>

        <Grid item xl={3} md={3} sm={12} xs={12}>
          <Autocomplete2
            options={TIME_FORMAT_OPTIONS}
            className={classes.filterfield}
            onChange={(ev, newVal) => newVal && handleReportFormChange(ReportID, 'timeFormat', newVal)}
            value={formValue.timeFormat ?? { Code: 'SECONDS' }}
            label="Time Format"
            compact="True"
            disableClearable
          />
        </Grid>

        <Grid item xl={3} md={3} sm={12} xs={12}>
          <TextField
            className={classes.targetfilterfield}
            fullWidth
            size="small"
            variant="outlined"
            label="Target"
            type="number"
            //inputProps={{ min: 1, step: 1 }}
            value={formValue.target ?? ''}
            onChange={handleTargetChange}
            error={targetError}
            helperText={targetError ? 'Enter a positive number' : ' '}
          />
        </Grid>

        <Grid item xl={3} md={3} sm={12} xs={12} className={classes.filterfield}>
          <FormControlLabel
            control={
              <Checkbox
                checked={!!formValue.isEventsRequired}
                onChange={(ev) => handleReportFormChange(ReportID, 'isEventsRequired', ev.target.checked)}
              />
            }
            label="Include Events"
          />
        </Grid>

      </Grid>
    </div>
  );
};

export default CADEventsData;